import { formatCurrencyUsd, labelForService } from '@/components/organisms/org-admin/usageFormat';
import { memberAllowance } from '../allowance';
import type {
  OrgMember,
  OrgRole,
  OrgServiceUsageReport,
  OrgServiceUsageReportByUsers,
  OrgUsageReport,
  OrgUsageTimeseries,
} from '../types';
import type { PersonSpendRow } from '../analysis/types';
import type {
  AdoptionBar,
  AgenticRow,
  LimitWatch,
  ModelUsageRow,
  OrgOverview,
  OverviewHeadline,
  OverviewMonthSelection,
  ResultTotals,
  RoleGroupRow,
  ServiceEconomyRow,
  ServiceStickinessRow,
  TimeSavedRow,
} from './types';

/**
 * The single seam between the org-admin REST payloads and the Overview screen.
 *
 * Pure, and deliberately so: every figure on the screen is derived here, so the components render
 * finished numbers and the whole derivation is testable without mounting anything. Same contract as
 * `buildUsageAnalysis` — see that file's header for the three API facts (three clocks, two sources,
 * no time dimension) that both adapters exist to absorb.
 *
 * The one rule this file enforces above all others: **nothing is invented.** Where the API cannot
 * answer a section of the Claude-console layout this screen is modelled on, the section is either
 * re-cut onto data that does exist (daily/weekly/monthly actives → the three real report windows) or
 * returns `null` and lets the card say so. There is no fixture here and no fallback that fabricates a
 * plausible shape — `analysis/trendFixture.ts` is the only module in this module tree allowed to do
 * that, it is gated out of production builds, and it is not imported from this file.
 */

/**
 * Starting assumptions for the time-saved estimate, in minutes of manual work per successful run.
 *
 * These are ESTIMATES the reader is expected to overwrite, and they are kept here rather than in the
 * component so the whole model arrives from one place. They are deliberately conservative: a number
 * that overstates the case is the fastest way to make the entire panel untrusted.
 */
const DEFAULT_MINUTES_PER_RUN: Record<string, number> = {
  testing: 25,
  playground: 45,
  'multi-step-agentic': 30,
  agentic_platform: 20,
  'release-scanner': 15,
  agents: 15,
};
const FALLBACK_MINUTES_PER_RUN = 10;

/** Compact currency for a hint line, where two figures must fit on one row. */
/*
 * Overview's money formatter defers to the shared one so this screen and Usage
 * round identically — they read the same payload and a reader moves between them.
 *
 * The magnitude rule is kept for LARGE figures only: past $100 the cents are noise
 * in a hint like "seats + automation", and dropping them is what keeps a tile label
 * on one line. Below that, precision is the whole point (see `formatCurrencyUsd`).
 */
function formatUsd(value: number): string {
  if (value >= 100) {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }
  return formatCurrencyUsd(value);
}

function safeShare(part: number, total: number): number {
  return total > 0 ? part / total : 0;
}

/** `a / b`, or `null` — 0 runs is "no data", not a ratio of zero. */
function ratio(part: number, whole: number): number | null {
  return whole > 0 ? part / whole : null;
}

/**
 * Whether a member did anything at all in the window that produced this row.
 *
 * Spend OR tokens, not spend alone: a member on a zero-rated model, or one whose spend rounds to
 * zero cents, is still an active user, and counting them as inactive understates adoption in exactly
 * the orgs most likely to be evaluating the product.
 */
function isActive(member: OrgUsageReport['members'][number]): boolean {
  return member.spend_usd_window > 0 || member.prompt_tokens > 0 || member.completion_tokens > 0;
}

// ── Headlines ───────────────────────────────────────────────────────────────

// ── Groups (roles) ──────────────────────────────────────────────────────────

function buildGroups(roles: OrgRole[] | undefined, members: OrgMember[] | undefined): RoleGroupRow[] {
  if (!roles?.length) return [];
  const countByRole = new Map<string, number>();
  for (const member of members ?? []) {
    if (!member.role_id) continue;
    countByRole.set(member.role_id, (countByRole.get(member.role_id) ?? 0) + 1);
  }
  return roles
    .map((role) => ({
      id: role.id,
      name: role.name,
      isDefault: role.is_default,
      members: countByRole.get(role.id) ?? 0,
    }))
    .sort((a, b) => b.members - a.members || a.name.localeCompare(b.name));
}

// ── Services ────────────────────────────────────────────────────────────────

function buildServiceEconomy(report: OrgServiceUsageReport | undefined): ServiceEconomyRow[] {
  if (!report) return [];
  return report.by_service
    .map((row) => ({
      service: row.service,
      label: labelForService(row.service),
      runs: row.run_count,
      costUsd: row.cost_usd,
      costPerRunUsd: ratio(row.cost_usd, row.run_count),
    }))
    .sort((a, b) => (b.costPerRunUsd ?? 0) - (a.costPerRunUsd ?? 0));
}

function buildAgentic(report: OrgServiceUsageReport | undefined): {
  rows: AgenticRow[];
  orgAverage: number | null;
} {
  if (!report) return { rows: [], orgAverage: null };
  const rows = report.by_service
    .map((row) => ({
      service: row.service,
      label: labelForService(row.service),
      events: row.event_count,
      runs: row.run_count,
      eventsPerRun: ratio(row.event_count, row.run_count),
    }))
    .sort((a, b) => (b.eventsPerRun ?? 0) - (a.eventsPerRun ?? 0));

  /*
   * The org figure is total events over total runs, NOT the mean of the per-service ratios.
   *
   * Averaging the ratios weights a service with three runs the same as one with three thousand, so a
   * single experimental service with a deep call chain would drag the headline number well above
   * anything the org actually experiences.
   */
  return { rows, orgAverage: ratio(report.event_count, report.run_count) };
}

/**
 * Distinct users per service, from the per-user service report.
 *
 * The payload is user-major (each user carries their own `by_service`), so the count has to be
 * inverted here. A user is counted for a service only if they actually ran something in it: the
 * backend zero-fills inactive members (`ObservabilityContext.user_summary/3`), so counting rows
 * would report every seat as a user of every service.
 */
function usersByService(report: OrgServiceUsageReportByUsers | undefined): Map<string, number> {
  const counts = new Map<string, number>();
  for (const user of report?.users ?? []) {
    for (const row of user.by_service) {
      if (row.run_count <= 0) continue;
      counts.set(row.service, (counts.get(row.service) ?? 0) + 1);
    }
  }
  return counts;
}

function buildStickiness(
  windowReport: OrgServiceUsageReportByUsers | undefined,
  recentReport: OrgServiceUsageReportByUsers | undefined,
): ServiceStickinessRow[] {
  const windowUsers = usersByService(windowReport);
  const recentUsers = usersByService(recentReport);

  return [...windowUsers.entries()]
    .map(([service, users]) => ({
      service,
      label: labelForService(service),
      recentUsers: recentUsers.get(service) ?? 0,
      windowUsers: users,
      ratio: ratio(recentUsers.get(service) ?? 0, users),
    }))
    .sort((a, b) => (b.ratio ?? 0) - (a.ratio ?? 0));
}

// ── Models ──────────────────────────────────────────────────────────────────

/**
 * Every model the org sent tokens to, aggregated across members.
 *
 * `by_model` is per member, so `users` is the number of members whose breakdown contains the model —
 * which is what makes this the honest answer to "who is using which model", rather than a token
 * total that one heavy user can dominate invisibly.
 */
function buildModels(report: OrgUsageReport | undefined): ModelUsageRow[] {
  const byModel = new Map<string, ModelUsageRow>();
  for (const member of report?.members ?? []) {
    for (const row of member.by_model) {
      const existing = byModel.get(row.model);
      if (existing) {
        existing.users += 1;
        existing.promptTokens += row.prompt_tokens;
        existing.completionTokens += row.completion_tokens;
        existing.spendUsd += row.spend_usd;
      } else {
        byModel.set(row.model, {
          model: row.model,
          users: 1,
          promptTokens: row.prompt_tokens,
          completionTokens: row.completion_tokens,
          spendUsd: row.spend_usd,
        });
      }
    }
  }
  return [...byModel.values()].sort((a, b) => b.spendUsd - a.spendUsd);
}

// ── Results & time saved ────────────────────────────────────────────────────

function buildResults(
  report: OrgUsageReport | undefined,
  serviceReport: OrgServiceUsageReport | undefined,
): ResultTotals {
  const runs = serviceReport?.run_count ?? 0;
  const failed = serviceReport?.failed_count ?? 0;
  return {
    runs,
    // Floored at 0: runs and failures are two aggregates over the same events, and a late-arriving
    // failure event can briefly make failures exceed runs. A negative count is never right.
    runsExcludingFailed: Math.max(0, runs - failed),
    failed,
    // Tokens come from LiteLLM, runs from observability — never summed across the two sources.
    inputTokens: report?.org_total.prompt_tokens ?? 0,
    outputTokens: report?.org_total.completion_tokens ?? 0,
    events: serviceReport?.event_count ?? 0,
    servicesUsed: (serviceReport?.by_service ?? []).filter((row) => row.run_count > 0).length,
  };
}

function buildTimeSaved(serviceReport: OrgServiceUsageReport | undefined): TimeSavedRow[] {
  if (!serviceReport) return [];
  return serviceReport.by_service
    // Failures excluded — NOT "successful runs only". Multiplying failed runs by a minutes-saved rate
    // credits the platform for work it did not deliver, which is the single easiest way to make this
    // estimate dishonest. But the remainder is not a success count either (see `runsExcludingFailed`
    // in `types.ts`), so the filter is what gets described, here and in the column header.
    .map((row) => ({
      service: row.service,
      label: labelForService(row.service),
      runs: Math.max(0, row.run_count - row.failed_count),
      defaultMinutes: DEFAULT_MINUTES_PER_RUN[row.service] ?? FALLBACK_MINUTES_PER_RUN,
    }))
    .filter((row) => row.runs > 0)
    .sort((a, b) => b.runs - a.runs);
}

// ── Cost ────────────────────────────────────────────────────────────────────

function buildLimits(report: OrgUsageReport | undefined, now: Date): LimitWatch {
  const members = report?.members ?? [];
  let atLimit = 0;
  let projectedToHit = 0;
  let noLimitSet = 0;

  for (const member of members) {
    if (member.budget_usd <= 0) {
      // Unlimited. Counted separately rather than as "0% used": a seat with no ceiling can never hit
      // one, and folding it into the healthy bucket hides that the ceiling was never set.
      noLimitSet += 1;
      continue;
    }
    /*
     * The backend's own remaining (plan + credits, on the cycle — #327) decides "at limit"; the
     * cycle-spend comparison is the fallback for a row without it. Both are the enforced clock.
     */
    const remaining = memberAllowance(member).remainingUsd;
    const exhausted = remaining === null ? member.spend_usd_cycle >= member.budget_usd : remaining <= 0;
    if (member.blocked || exhausted) {
      atLimit += 1;
      continue;
    }

    /*
     * Per-seat projection, from this seat's own cycle clock.
     *
     * Both ends of the cycle are needed to measure elapsed time honestly; without a start date there
     * is no burn rate, so the seat is simply not projected rather than projected off an assumed
     * 30-day cycle. A guessed denominator here produces a confident count of seats "about to be cut
     * off" that is pure arithmetic.
     */
    if (!member.cycle_started_at || !member.resets_at) continue;
    const startedMs = Date.parse(member.cycle_started_at);
    const resetsMs = Date.parse(member.resets_at);
    if (Number.isNaN(startedMs) || Number.isNaN(resetsMs) || resetsMs <= startedMs) continue;

    const elapsedMs = now.getTime() - startedMs;
    if (elapsedMs <= 0) continue;
    const burnPerMs = member.spend_usd_cycle / elapsedMs;
    const projected = member.spend_usd_cycle + burnPerMs * (resetsMs - now.getTime());
    if (projected >= member.budget_usd) projectedToHit += 1;
  }

  return { totalSeats: members.length, atLimit, projectedToHit, noLimitSet };
}

/**
 * Spend distribution by decile of member, highest spender first.
 *
 * Deciles of MEMBERS, not of spend: each bucket holds a tenth of the seats and reports what share of
 * the money they account for. With fewer than ten seats the buckets are uneven by construction —
 * `members` is carried on every bucket so the chart can say so rather than implying a population it
 * does not have.
 */

/**
 * One row per member: window spend, cycle spend against budget, and the sort order every "top
 * members"/"concentration" card downstream expects (heaviest window spender first). Mode-agnostic
 * — mode-agnostic, since a member row means the same thing regardless
 * of which report scoped it.
 */
function buildPeople(reportMembers: OrgUsageReport['members']): PersonSpendRow[] {
  return reportMembers
    .map((member) => {
      const hasBudget = member.budget_usd > 0;
      return {
        userId: member.user_id,
        label: member.email ?? member.user_id,
        windowUsd: member.spend_usd_window,
        shareOfTotal: safeShare(
          member.spend_usd_window,
          reportMembers.reduce((sum, m) => sum + m.spend_usd_window, 0),
        ),
        cycleUsd: member.spend_usd_cycle,
        budgetUsd: member.budget_usd,
        withinBudgetUsd: hasBudget
          ? Math.min(member.spend_usd_cycle, member.budget_usd)
          : member.spend_usd_cycle,
        overageUsd: hasBudget ? Math.max(0, member.spend_usd_cycle - member.budget_usd) : 0,
        utilisation: hasBudget ? member.spend_usd_cycle / member.budget_usd : null,
        blocked: member.blocked,
        status: member.status,
      };
    })
    .sort((a, b) => b.windowUsd - a.windowUsd);
}

// ── Root ────────────────────────────────────────────────────────────────────

// ── Period mode ─────────────────────────────────────────────────────────────
//
// The only mode. `buildOverview`, which read the three allowlisted DAY windows, is gone: Overview
// and Usage now share one calendar-period control (`useReportingMonth`), so the day-window adapter
// had no caller. Two things were genuinely lost with it and are recorded here so nobody rediscovers
// them as bugs:
//
//   - the adoption funnel NESTED (active in 30d ⊇ 15d ⊇ 7d), because each window contained the
//     next. Two discrete calendar months do not, so `current`/`previous` is a side-by-side
//     comparison rather than a funnel. `this_quarter` recovers the nesting — `current` is a real
//     subset of a rolling quarter — which is why the quarter's funnel has its own bucket list;
//   - the headline spend delta could be derived on any window by subtracting it from the next
//     larger one. Months cannot be subtracted, so the delta comes from a direct daily-rate diff
//     (`changeVsPreviousMonth`) and exists for `current` only — see `comparisonMonthFor`.
//
// Everything above this line is mode-agnostic and was reused verbatim: each helper operates on
// whichever single report or service report it is handed and never cared how that report was
// scoped. `buildStickiness` in particular needed no sibling — it ratios any two per-user reports,
// so "this quarter's users who were also active this month" is the same call with a different
// second argument.

/**
 * The month the headline spend delta is measured against — or `null` for "no delta is derivable".
 *
 * Only `current` has one. The other two do not, for two different reasons, and both are real:
 *
 *   - `previous` would need the month before it, which the API does not expose (`?month=` accepts
 *     current, previous and this_quarter only).
 *   - `this_quarter` is a ROLLING three-calendar-month window ending today, so it CONTAINS both of
 *     the other options. Diffing it against `previous` would be comparing a period against a period
 *     inside itself — the sign would be meaningless. It is the same trap the day-window comment
 *     above describes for 15d vs 30d, except there the overlap was the mechanism and here it is the
 *     defect, because months do not nest the way the day windows did.
 *
 * A `null` delta renders as an explicit absence, never as 0% or a dash — see `OverviewStatTiles`.
 */
function comparisonMonthFor(month: OverviewMonthSelection): OverviewMonthSelection | null {
  return month === 'current' ? 'previous' : null;
}

/**
 * The month the STICKINESS ratio is measured against, which is a different question from the delta
 * above and therefore has a different answer.
 *
 * Stickiness asks "of the people active in the selected period, how many were also active in a
 * narrower, more recent one" — it wants a comparator that OVERLAPS, where the delta wants one that
 * does not. So the quarter's comparator is `current`: this month is a genuine subset of a rolling
 * quarter, which makes "of everyone active this quarter, how many were active this month" the
 * best-founded reading of this ratio anywhere on the screen.
 *
 * `previous` is given `current` for the same reason it always was — a closed month against the one
 * running now reads as retention. This used to be routed through `comparisonMonthFor`, which
 * returned `null` for `previous`, so the card's own description promised a comparison the adapter
 * never made and every row's ratio came back empty. Splitting the two is what fixes that.
 */
function stickinessComparatorFor(month: OverviewMonthSelection): OverviewMonthSelection {
  return month === 'current' ? 'previous' : 'current';
}

/** The buckets the funnel plots, widest first, for each selectable period. */
const ADOPTION_MONTHS: Record<OverviewMonthSelection, readonly OverviewMonthSelection[]> = {
  /*
   * A quarter gets a genuinely NESTED funnel — every member active this month is also active this
   * quarter, so the bars narrow monotonically and the shape means what a funnel's shape implies.
   *
   * Worth stating because the other two rows do NOT nest: a member active last month but not this
   * month is absent from the "this month" bar, so those two bars are a side-by-side comparison
   * wearing a funnel's clothes. That is a known compromise of the two-month view, and it is the
   * strongest argument for the quarter being the better default reading of adoption.
   */
  this_quarter: ['this_quarter', 'current'],
  current: ['current', 'previous'],
  previous: ['current', 'previous'],
};

/** Title-cased for the toolbar and the tile rail's accessible name. */
const PERIOD_LABEL: Record<OverviewMonthSelection, string> = {
  current: 'This month',
  previous: 'Last month',
  this_quarter: 'This quarter',
};

const ADOPTION_MONTH_LABEL: Record<OverviewMonthSelection, string> = {
  current: 'Active this month',
  previous: 'Active last month',
  this_quarter: 'Active this quarter',
};

function buildAdoptionForMonth(
  reports: Partial<Record<OverviewMonthSelection, OrgUsageReport>>,
  seatCount: number,
  month: OverviewMonthSelection,
): AdoptionBar[] {
  const bars: AdoptionBar[] = [
    { id: 'all', label: 'All members', members: seatCount, shareOfAll: seatCount > 0 ? 1 : 0 },
  ];

  for (const bucket of ADOPTION_MONTHS[month]) {
    const report = reports[bucket];
    // Same reasoning as buildAdoption: an unresolved request is omitted, not plotted as zero.
    if (!report) continue;
    const active = report.members.filter(isActive).length;
    bars.push({
      id: `active-${bucket}`,
      label: ADOPTION_MONTH_LABEL[bucket],
      members: active,
      shareOfAll: safeShare(active, seatCount),
    });
  }

  return bars;
}

/**
 * Change vs. the immediately-preceding calendar month, as a daily-rate comparison — `current` is
 * a partial month (1st through today) and `previous` is a full closed one, so comparing raw
 * totals would manufacture a change out of the two periods having different lengths, the same
 * problem `changeVsPrevious` solves for overlapping day-windows.
 */
function changeVsPreviousMonth(
  currentTotal: number,
  previousTotal: number | undefined,
  daysElapsedInCurrentMonth: number,
  daysInPreviousMonth: number,
): number | null {
  if (previousTotal === undefined) return null;
  if (daysElapsedInCurrentMonth <= 0 || daysInPreviousMonth <= 0) return null;
  const currentDaily = currentTotal / daysElapsedInCurrentMonth;
  const previousDaily = previousTotal / daysInPreviousMonth;
  if (previousDaily <= 0) return null;
  return (currentDaily - previousDaily) / previousDaily;
}


export interface BuildOverviewForMonthInput {
  month: OverviewMonthSelection;
  /** Both months, unconditionally — mirrors `adoptionReports` fetching every day-window regardless
   * of which one is selected, since the funnel/comparison needs both regardless of which is primary. */
  reports: Partial<Record<OverviewMonthSelection, OrgUsageReport>>;
  /** The PRIMARY (selected) month's service report — `previous`'s, when viewing last month. */
  serviceReport?: OrgServiceUsageReport;
  /** Both months' per-user service reports, for the adoption funnel and the stickiness ratio. */
  serviceUsersByMonth: Partial<Record<OverviewMonthSelection, OrgServiceUsageReportByUsers>>;
  roles?: OrgRole[];
  members?: OrgMember[];
  /** The PRIMARY month's timeseries. */
  timeseries?: OrgUsageTimeseries;
  now: Date;
}

export function buildOverviewForMonth({
  month,
  reports,
  serviceReport,
  serviceUsersByMonth,
  roles,
  members,
  timeseries,
  now,
}: BuildOverviewForMonthInput): OrgOverview {
  const report = reports[month];
  const comparisonMonth = comparisonMonthFor(month);
  const comparisonReport = comparisonMonth ? reports[comparisonMonth] : undefined;
  const stickinessMonth = stickinessComparatorFor(month);

  const reportMembers = report?.members ?? [];
  const seatCount = members?.length ?? reportMembers.length;
  const activeInWindow = reportMembers.filter(isActive).length;
  const people = buildPeople(reportMembers);
  const results = buildResults(report, serviceReport);

  const memberSpend = report?.org_total.spend_usd ?? 0;
  const automationSpend = report?.automation?.spend_usd_window ?? 0;
  const windowSpend = memberSpend + automationSpend;

  const daysInPreviousCalendarMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  /*
   * Only read when a comparison exists, which is `current` only — see `comparisonMonthFor`. Kept as
   * a plain expression rather than a branch because the other two periods never reach the delta.
   */
  const daysElapsedInCurrentMonth = month === 'current' ? now.getDate() : daysInPreviousCalendarMonth;

  const headlines: OverviewHeadline[] = [
    {
      id: 'active-members',
      label: 'Active members',
      value: String(activeInWindow),
      hint: `out of ${seatCount} seat${seatCount === 1 ? '' : 's'}`,
      changeRatio: null,
      risePolarity: 'good',
    },
    {
      id: 'runs',
      label: 'Runs',
      value: results.runs.toLocaleString(),
      hint: 'across all services',
      changeRatio: null,
      risePolarity: 'good',
    },
    {
      id: 'services',
      label: 'Services used',
      value: String(results.servicesUsed),
      hint: 'with at least one run',
      changeRatio: null,
      risePolarity: 'neutral',
    },
    {
      id: 'spend',
      label: 'Total spend',
      // The SHARED formatter, not a local one. This tile inlined its own
      // `maximumFractionDigits: 2` and so rendered $1.56 for the same $1.55846944
      // that Top spenders — three tiles away, via formatCurrencyUsd — rendered as
      // $1.5585. Same payload, same screen, two different numbers.
      value: formatCurrencyUsd(windowSpend),
      hint:
        automationSpend > 0
          ? `${formatUsd(memberSpend)} seats + ${formatUsd(automationSpend)} automation`
          : 'all services',
      changeRatio: comparisonReport
        ? changeVsPreviousMonth(
            memberSpend,
            comparisonReport.org_total.spend_usd,
            daysElapsedInCurrentMonth,
            daysInPreviousCalendarMonth,
          )
        : null,
      risePolarity: 'bad',
    },
  ];

  return {
    window: { label: PERIOD_LABEL[month] },
    headlines,
    adoption: buildAdoptionForMonth(reports, seatCount, month),
    groups: buildGroups(roles, members),
    topMembers: people,
    // The STICKINESS comparator, not the delta's — a ratio wants an overlapping period where a
    // delta wants a disjoint one. See `stickinessComparatorFor`.
    stickiness: buildStickiness(serviceUsersByMonth[month], serviceUsersByMonth[stickinessMonth]),
    serviceEconomy: buildServiceEconomy(serviceReport),
    models: buildModels(report),
    agentic: buildAgentic(serviceReport),
    results,
    timeSaved: buildTimeSaved(serviceReport),
    limits: buildLimits(report, now),
    /*
     * No `concentration`, `cumulative` or `budgetUsd` here any more.
     *
     * Spend concentration moved to Usage beside the per-member table it summarises, and the
     * cumulative chart moved to Usage beside the daily trend — they are one question at two
     * resolutions. `dev`'s closed-month insight travelled with the chart rather than being dropped:
     * a `previous` month is closed, so its series is pure actuals and must not be given a projected
     * tail. That now lives in `analysis/buildUsageAnalysis`.
     */
    provenance: {
      partialSources: report?.partial ?? [],
      hasTimeseries: (timeseries?.points.length ?? 0) > 0,
    },
  };
}
